import type { PrivacyExportFile, StaffPrivacyApi } from "@lafayette/api-client";
import { createContext, type ReactNode, useContext } from "react";

// What the privacy screens need from outside: the typed client, the clock and the way a downloaded file is handed to the person. The admin app
// supplies the real ones (`admin-runtime.ts`); a test or the development harness supplies fakes.

/** How often the list is read again while a request is approved or running, and for how long (the page sets both; a test sets small ones). */
export type PrivacyPolling = Readonly<{ everyMs: number; limitMs: number }>;

export type PrivacyRuntime = Readonly<{
  api: StaffPrivacyApi;
  /** Optional: the polling of the list. The defaults are 5 seconds and 2 minutes. */
  polling?: PrivacyPolling;
  now: () => Date;
  /** Hands the export file to the person (a browser download). The file is held in memory only for this call. */
  saveFile: (file: PrivacyExportFile) => void;
}>;

const RuntimeContext = createContext<PrivacyRuntime | null>(null);

export function PrivacyRuntimeProvider({
  value,
  children,
}: Readonly<{ value: PrivacyRuntime; children: ReactNode }>) {
  return <RuntimeContext.Provider value={value}>{children}</RuntimeContext.Provider>;
}

export function usePrivacyRuntime(): PrivacyRuntime {
  const runtime = useContext(RuntimeContext);
  if (runtime === null) throw new Error("The privacy pages need a PrivacyRuntimeProvider.");
  return runtime;
}
